/**
 * Timber Sizing — CRM Estimating tool page.
 * Pure calc lives in src/utils/timberSizingCalc.ts.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Info, Ruler, RotateCcw } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import {
  applyFloorPreset,
  applyRoofPreset,
  defaultInputs,
  findMaxSpan,
  findMinSection,
  formatSpanFtIn,
  GRADE_OPTIONS,
  normalizeTimberGrade,
  SPECIES_OPTIONS,
  STOCK_SECTIONS,
  TimberGradeId,
  TimberMoisture,
  TimberSizingInputs,
  TimberSizingMode,
  TimberSpeciesId,
} from '../utils/timberSizingCalc';
import './TimberSizingCalculator.css';

const STORAGE_KEY = 'timberSizingCalculator:v1';

type Persisted = Omit<
  TimberSizingInputs,
  'sizeBasis' | 'Cr' | 'useDimensionLumberValues'
>;

function toPersisted(p: TimberSizingInputs): Persisted {
  const {
    sizeBasis: _a,
    Cr: _b,
    useDimensionLumberValues: _c,
    ...rest
  } = p;
  return rest;
}

function fromPersisted(raw: Partial<Persisted> | null | undefined): TimberSizingInputs {
  const d = defaultInputs();
  if (!raw || typeof raw !== 'object') return d;
  const species = SPECIES_OPTIONS.some((s) => s.id === raw.species) ? (raw.species as TimberSpeciesId) : d.species;
  const grade = normalizeTimberGrade(raw.grade);
  const moisture: TimberMoisture = raw.moisture === 'green' || raw.moisture === 'dry' ? raw.moisture : d.moisture;
  return {
    ...d,
    mode: raw.mode === 'sectionToSpan' ? 'sectionToSpan' : 'spanToSection',
    species,
    grade,
    moisture,
    clearSpanFt: typeof raw.clearSpanFt === 'number' ? raw.clearSpanFt : d.clearSpanFt,
    nominalB: typeof raw.nominalB === 'number' ? raw.nominalB : d.nominalB,
    nominalD: typeof raw.nominalD === 'number' ? raw.nominalD : d.nominalD,
    loadMode: raw.loadMode === 'plf' ? 'plf' : 'psfTrib',
    deadPsf: typeof raw.deadPsf === 'number' ? raw.deadPsf : d.deadPsf,
    livePsf: typeof raw.livePsf === 'number' ? raw.livePsf : d.livePsf,
    tributaryWidthFt: typeof raw.tributaryWidthFt === 'number' ? raw.tributaryWidthFt : d.tributaryWidthFt,
    loadPlf: typeof raw.loadPlf === 'number' ? raw.loadPlf : d.loadPlf,
    livePlf: typeof raw.livePlf === 'number' ? raw.livePlf : d.livePlf,
    deflectionLimit: raw.deflectionLimit === 240 ? 240 : 360,
    Cd: typeof raw.Cd === 'number' ? raw.Cd : d.Cd,
  };
}

function loadPersisted(): TimberSizingInputs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultInputs();
    return fromPersisted(JSON.parse(raw) as Partial<Persisted>);
  } catch {
    return defaultInputs();
  }
}

function ratioClass(r: number): string {
  if (r <= 0.85) return 'ts-ratio--ok';
  if (r <= 1) return 'ts-ratio--tight';
  return 'ts-ratio--fail';
}

const TimberSizingCalculator: React.FC = () => {
  const [p, setP] = useState<TimberSizingInputs>(() => loadPersisted());

  const persist = useCallback((next: TimberSizingInputs) => {
    setP(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersisted(next)));
    } catch {
      /* ignore */
    }
  }, []);

  const update = (patch: Partial<TimberSizingInputs>) => {
    persist({ ...p, ...patch });
  };

  const onSpecies = (id: TimberSpeciesId) => {
    const opt = SPECIES_OPTIONS.find((s) => s.id === id);
    persist({
      ...p,
      species: id,
      moisture: opt ? opt.defaultMoisture : p.moisture,
    });
  };

  const spanResult = useMemo(() => {
    if (p.mode !== 'spanToSection') return null;
    return findMinSection(p);
  }, [p]);

  const maxSpanResult = useMemo(() => {
    if (p.mode !== 'sectionToSpan') return null;
    return findMaxSpan(p);
  }, [p]);

  const activeCheck = p.mode === 'spanToSection' ? spanResult?.check : maxSpanResult?.checkAtMax;

  const setMode = (mode: TimberSizingMode) => update({ mode });

  const reset = () => persist(defaultInputs());

  return (
    <div className="ts-page">
      <AppHeader />
      <div className="ts-inner">
        <header className="ts-header">
          <div className="ts-header-title">
            <Link to="/tools" className="ts-back">
              <ArrowLeft size={18} /> Tools
            </Link>
            <h1>
              <Ruler size={22} strokeWidth={2} aria-hidden />
              Timber Sizing
            </h1>
            <p className="ts-sub">
              Simple-span sawn timber beam sizing — span → min stock section, or section → max clear span.
              Analysis uses half-under dimensions (nominal − ½″ each face).
            </p>
          </div>
          <div className="ts-header-actions">
            <button type="button" className="ts-btn ts-btn--ghost" onClick={reset}>
              <RotateCcw size={16} /> Reset
            </button>
          </div>
        </header>

        <div className="ts-disclaimer" role="note">
          <Info size={18} aria-hidden />
          <div>
            <strong>Estimating / concept aid only.</strong> Not a PE stamp, NDS substitute, or construction
            document. Checks bending, shear, and deflection for a simple-span rectangular sawn beam only —
            does not check connections, joinery, bearing, notches, holes, lateral bracing, or load path.
            Verify with a licensed engineer for final design.
          </div>
        </div>

        <div className="ts-mode-tabs" role="tablist" aria-label="Sizing mode">
          <button
            type="button"
            role="tab"
            aria-selected={p.mode === 'spanToSection'}
            className={`ts-mode-tab${p.mode === 'spanToSection' ? ' ts-mode-tab--active' : ''}`}
            onClick={() => setMode('spanToSection')}
          >
            Span → min section
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={p.mode === 'sectionToSpan'}
            className={`ts-mode-tab${p.mode === 'sectionToSpan' ? ' ts-mode-tab--active' : ''}`}
            onClick={() => setMode('sectionToSpan')}
          >
            Section → max span
          </button>
        </div>

        <div className="ts-grid">
          <section className="ts-card" aria-label="Inputs">
            <h2 className="ts-card-title">Inputs</h2>

            <div className="ts-presets">
              <button type="button" className="ts-chip" onClick={() => persist(applyFloorPreset(p))}>
                Floor preset (40/10, L/360)
              </button>
              <button type="button" className="ts-chip" onClick={() => persist(applyRoofPreset(p))}>
                Roof preset (20/15, L/240, Cd 1.15)
              </button>
            </div>

            {p.mode === 'spanToSection' ? (
              <label className="ts-label">
                <span className="ts-label-text">Clear span (ft)</span>
                <input
                  type="number"
                  className="ts-input"
                  min={1}
                  step={0.1}
                  value={p.clearSpanFt}
                  onChange={(e) => update({ clearSpanFt: parseFloat(e.target.value) || 0 })}
                />
              </label>
            ) : (
              <label className="ts-label">
                <span className="ts-label-text">Nominal section</span>
                <select
                  className="ts-input"
                  value={`${p.nominalB}x${p.nominalD}`}
                  onChange={(e) => {
                    const [b, d] = e.target.value.split('x').map(Number);
                    update({ nominalB: b, nominalD: d });
                  }}
                >
                  {STOCK_SECTIONS.map((s) => (
                    <option key={s.label} value={`${s.b}x${s.d}`}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="ts-row">
              <label className="ts-label">
                <span className="ts-label-text">Species</span>
                <select
                  className="ts-input"
                  value={p.species}
                  onChange={(e) => onSpecies(e.target.value as TimberSpeciesId)}
                >
                  {SPECIES_OPTIONS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ts-label">
                <span className="ts-label-text">Grade</span>
                <select
                  className="ts-input"
                  value={p.grade}
                  onChange={(e) => update({ grade: e.target.value as TimberGradeId })}
                >
                  {GRADE_OPTIONS.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="ts-row">
              <label className="ts-label">
                <span className="ts-label-text">Moisture</span>
                <select
                  className="ts-input"
                  value={p.moisture}
                  onChange={(e) => update({ moisture: e.target.value as TimberMoisture })}
                >
                  <option value="dry">KD / dry (≤19% MC)</option>
                  <option value="green">Green / wet (&gt;19% MC)</option>
                </select>
              </label>
              <label className="ts-label">
                <span className="ts-label-text">Duration Cd</span>
                <select
                  className="ts-input"
                  value={p.Cd}
                  onChange={(e) => update({ Cd: parseFloat(e.target.value) || 1 })}
                >
                  <option value={1}>1.00 — permanent / floor live</option>
                  <option value={1.15}>1.15 — snow / roof live</option>
                  <option value={1.25}>1.25 — construction</option>
                </select>
              </label>
            </div>

            <fieldset className="ts-fieldset">
              <legend>Loads</legend>
              <div className="ts-seg">
                <button
                  type="button"
                  className={`ts-seg-btn${p.loadMode === 'psfTrib' ? ' ts-seg-btn--active' : ''}`}
                  onClick={() => update({ loadMode: 'psfTrib' })}
                >
                  psf + tributary
                </button>
                <button
                  type="button"
                  className={`ts-seg-btn${p.loadMode === 'plf' ? ' ts-seg-btn--active' : ''}`}
                  onClick={() => update({ loadMode: 'plf' })}
                >
                  Direct plf
                </button>
              </div>
              {p.loadMode === 'psfTrib' ? (
                <div className="ts-row ts-row--3">
                  <label className="ts-label">
                    <span className="ts-label-text">Dead (psf)</span>
                    <input
                      type="number"
                      className="ts-input"
                      min={0}
                      value={p.deadPsf}
                      onChange={(e) => update({ deadPsf: parseFloat(e.target.value) || 0 })}
                    />
                  </label>
                  <label className="ts-label">
                    <span className="ts-label-text">Live (psf)</span>
                    <input
                      type="number"
                      className="ts-input"
                      min={0}
                      value={p.livePsf}
                      onChange={(e) => update({ livePsf: parseFloat(e.target.value) || 0 })}
                    />
                  </label>
                  <label className="ts-label">
                    <span className="ts-label-text">Trib (ft)</span>
                    <input
                      type="number"
                      className="ts-input"
                      min={0}
                      step={0.1}
                      value={p.tributaryWidthFt}
                      onChange={(e) => update({ tributaryWidthFt: parseFloat(e.target.value) || 0 })}
                    />
                  </label>
                </div>
              ) : (
                <div className="ts-row">
                  <label className="ts-label">
                    <span className="ts-label-text">Total load (plf)</span>
                    <input
                      type="number"
                      className="ts-input"
                      min={0}
                      value={p.loadPlf}
                      onChange={(e) => update({ loadPlf: parseFloat(e.target.value) || 0 })}
                    />
                  </label>
                  <label className="ts-label">
                    <span className="ts-label-text">Live portion (plf)</span>
                    <input
                      type="number"
                      className="ts-input"
                      min={0}
                      value={p.livePlf}
                      onChange={(e) => update({ livePlf: parseFloat(e.target.value) || 0 })}
                    />
                  </label>
                </div>
              )}
            </fieldset>

            <label className="ts-label">
              <span className="ts-label-text">Deflection limit</span>
              <select
                className="ts-input"
                value={p.deflectionLimit}
                onChange={(e) => update({ deflectionLimit: Number(e.target.value) === 240 ? 240 : 360 })}
              >
                <option value={360}>L/360 — floors (live)</option>
                <option value={240}>L/240 — roofs (live)</option>
              </select>
            </label>

            <p className="ts-hint">
              Section basis: half-under (e.g. 8×10 stock analyzed as 7.5″ × 9.5″). Design values from NDS
              Supplement Table 4D — #2, #1, and Select Structural.
            </p>
          </section>

          <section className="ts-card ts-card--result" aria-label="Results">
            <h2 className="ts-card-title">Result</h2>

            {p.mode === 'spanToSection' && spanResult && (
              <div className={`ts-result-banner${spanResult.ok ? ' ts-result-banner--ok' : ' ts-result-banner--fail'}`}>
                {spanResult.ok && spanResult.section ? (
                  <>
                    <div className="ts-result-big">{spanResult.section.label}</div>
                    <div className="ts-result-msg">{spanResult.message}</div>
                  </>
                ) : (
                  <div className="ts-result-msg">{spanResult.message}</div>
                )}
              </div>
            )}

            {p.mode === 'sectionToSpan' && maxSpanResult && (
              <div className={`ts-result-banner${maxSpanResult.ok ? ' ts-result-banner--ok' : ' ts-result-banner--fail'}`}>
                {maxSpanResult.ok && maxSpanResult.maxClearSpanFt != null ? (
                  <>
                    <div className="ts-result-big">{formatSpanFtIn(maxSpanResult.maxClearSpanFt)}</div>
                    <div className="ts-result-msg">{maxSpanResult.message}</div>
                  </>
                ) : (
                  <div className="ts-result-msg">{maxSpanResult.message}</div>
                )}
              </div>
            )}

            {activeCheck && (
              <div className="ts-checks">
                <div className="ts-check-meta">
                  Analysis section {activeCheck.actualB}″ × {activeCheck.actualD}″ · {activeCheck.sizeClass} ·
                  w = {activeCheck.wTotalPlf} plf (live {activeCheck.wLivePlf}) · Fb′ {activeCheck.FbPrime} · Fv′{' '}
                  {activeCheck.FvPrime} · E′ {(activeCheck.EPrime / 1e6).toFixed(2)}E6
                </div>
                <table className="ts-table">
                  <thead>
                    <tr>
                      <th>Check</th>
                      <th>Demand</th>
                      <th>Limit</th>
                      <th>Ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Bending</td>
                      <td>{activeCheck.fb} psi</td>
                      <td>{activeCheck.FbPrime} psi</td>
                      <td className={ratioClass(activeCheck.ratios.bending)}>
                        {activeCheck.ratios.bending.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>Shear</td>
                      <td>{activeCheck.fv} psi</td>
                      <td>{activeCheck.FvPrime} psi</td>
                      <td className={ratioClass(activeCheck.ratios.shear)}>
                        {activeCheck.ratios.shear.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>Deflection</td>
                      <td>{activeCheck.deltaIn.toFixed(3)}″</td>
                      <td>{activeCheck.deltaAllowIn.toFixed(3)}″</td>
                      <td className={ratioClass(activeCheck.ratios.deflection)}>
                        {activeCheck.ratios.deflection.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="ts-hint">
                  Governing: <strong>{activeCheck.governing}</strong>
                  {activeCheck.passes ? ' — passes' : ' — does not pass'}
                </p>
              </div>
            )}

            <details className="ts-help">
              <summary>How this maps to published span tables</summary>
              <p>
                Unit tests compare the engine to AWC <em>Span Tables for Joists and Rafters</em> (2024) Table F-2
                using dressed dimension lumber (not half-under timber). Production defaults stay half-under per
                shop practice. Full notes: <code>docs/TIMBER_SIZING.md</code>.
              </p>
            </details>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TimberSizingCalculator;
